import { useCallback } from 'react';
import { isUndefined } from 'lodash';

import { VideoConsultationReminderNotificationEnabledQuery } from '@dreryk/egabinet-phone-notification-frontend-api';
import { PatientSimple, PatientSimpleQuery } from '@dreryk/egabinet-patient-frontend-api';

import {
   ModalCancelButton,
   ModalCloseButton,
   ModalConfirmButton,
   ModalWidth,
   ShowModalEventInterceptor,
} from '@dreryk/react-components';
import { performCqrsQueryGet } from '@dreryk/command-query-react-common';
import { Slot } from '@dreryk/watermelon';

import { protectFeature } from '~libs.shared.system';
import {
   PatientContactDataUpdateAction,
   PatientContactDataUpdateForm,
} from '~libs.desktop.shared.patient-contact-data-update';

import { translationKeys } from '../translations';

const t = translationKeys;

type VideoConsultationPatientContactUpdateShowModalEventInterceptorProps = Readonly<{
   shouldShowModal: boolean;
   patientId: number | null;
   children: JSX.Element;
   onClose?: VoidFunction;
}>;

// eslint-disable-next-line max-lines-per-function
export const VideoConsultationPatientContactUpdateShowModalEventInterceptor = (
   props: VideoConsultationPatientContactUpdateShowModalEventInterceptorProps
): JSX.Element => {
   const performingVideoConsultationsAvailable = protectFeature('performingVideoConsultations');

   const shouldShowModal = useCallback(async () => {
      if (!props.shouldShowModal || !props.patientId || !performingVideoConsultationsAvailable) {
         return false;
      }

      const patient = await performCqrsQueryGet<PatientSimple>(() => {
         const query = new PatientSimpleQuery();
         query.id = Slot.of(props.patientId);
         return query;
      });

      const videoConsultationReminderNotificationEnabled = await performCqrsQueryGet<boolean>(
         () => new VideoConsultationReminderNotificationEnabledQuery()
      );

      const phoneNumberRequired = isUndefined(patient.phoneNumber) && videoConsultationReminderNotificationEnabled;
      const emailAddressRequired = isUndefined(patient.emailAddress) && !videoConsultationReminderNotificationEnabled;

      if (phoneNumberRequired || emailAddressRequired) {
         return {
            patient: patient,
            phoneNumberRequired: phoneNumberRequired,
            emailAddressRequired: emailAddressRequired,
         };
      }

      return false;
   }, [props.patientId, props.shouldShowModal, performingVideoConsultationsAvailable]);

   return (
      <ShowModalEventInterceptor
         shouldShowModal={shouldShowModal}
         testId={'videoConsultationPatientContactUpdateShowModal'}
         title={t.fillInPatientContactData}
         modalWidth={ModalWidth.NORMAL}
         footerItemsHidden
         content={({ resolve, closeModal, result: { patient, emailAddressRequired, phoneNumberRequired } }) => (
            <PatientContactDataUpdateForm
               patientContactData={patient}
               emailAddressRequired={emailAddressRequired}
               phoneNumberRequired={phoneNumberRequired}
            >
               {({ handleSubmit }) => {
                  return (
                     <>
                        <PatientContactDataUpdateAction patientId={patient.id} onSuccess={resolve}>
                           {({ updatePatientContactData }) => (
                              <ModalConfirmButton label={t.saveData} onClick={handleSubmit(updatePatientContactData)} />
                           )}
                        </PatientContactDataUpdateAction>

                        <ModalCloseButton
                           onClick={() => {
                              closeModal();
                              props.onClose?.();
                           }}
                        />
                        <ModalCancelButton
                           onClick={() => {
                              closeModal();
                              props.onClose?.();
                           }}
                        />
                     </>
                  );
               }}
            </PatientContactDataUpdateForm>
         )}
      >
         {props.children}
      </ShowModalEventInterceptor>
   );
};
